import { useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import { cn } from "@/lib/utils";
import {
  ColumnResizeHandle,
  ColumnResizeOverlay,
  pushWidth,
  useColumnDrag,
} from "@/components/DataTable/columnResize";
import {
  FIELD_DELETE_COL_W,
  FIELD_UTILITY_COL_W,
  MIN_FIELD_COL_W,
  NARROW_FIELD_COL_W,
  computeFieldColumnWidths,
  parseStoredWidths,
} from "./tableColumnWidths";
import { Switch } from "@/components/shadcn/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import { Calendar as CalendarComponent } from "@/components/shadcn/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import {
  Calendar,
  Hash,
  Link2,
  List,
  Mail,
  Phone,
  Plus,
  Rows3,
  ToggleLeft,
  Trash2,
  Type,
  type LucideIcon,
} from "lucide-react";
import type { TableSelectSource } from "@/generated";
import type { TableColumn } from "./types";
import { formatDateOnly, parseDateOnly } from "./dateOnly";
import { LookupCellCombobox } from "./LookupCellCombobox";
import { isEmpty, sentenceCase, validateValue } from "./validation";
import { NoxusEmptyBlock } from "../UI/NoxusEmptyBlock";
import { NoxusTooltip } from "../UI/NoxusTooltip";

type Row = Record<string, unknown>;

const lookupSourceOf = (column: TableColumn): TableSelectSource | null =>
  column.source?.kind === "table" ? column.source : null;

interface TableFieldGridProps {
  columns: TableColumn[];
  rows: Row[];
  onChange: (rows: Row[]) => void;
  readOnly?: boolean;
  // localStorage key for persisting user-resized column widths (per field).
  // Without it, resizing still works but resets on unmount.
  widthStorageKey?: string;
}

const defaultForType = (type: TableColumn["type"]): unknown => {
  switch (type) {
    case "boolean":
      return false;
    case "number":
      return null;
    default:
      return "";
  }
};

// Mirrors COLUMN_TYPE_ICONS on the Tables building block where types overlap.
const FIELD_TYPE_ICONS: Record<string, LucideIcon> = {
  text: Type,
  number: Hash,
  boolean: ToggleLeft,
  date: Calendar,
  select: List,
  email: Mail,
  phone: Phone,
  url: Link2,
};

const HeaderIcon = ({ column }: { column: TableColumn }) => {
  // A lookup column points at another table — closer to the block's
  // "reference" than to a plain select.
  const Icon = lookupSourceOf(column)
    ? Link2
    : FIELD_TYPE_ICONS[resolveColType(column)];
  return Icon ? (
    <Icon className="size-3.5 shrink-0 text-muted-foreground" />
  ) : null;
};

const inputTypeFor = (type: TableColumn["type"]): string => {
  switch (type) {
    case "number":
      return "number";
    case "date":
      return "date";
    case "email":
      return "email";
    case "url":
      return "url";
    case "phone":
      return "tel";
    default:
      return "text";
  }
};

// Tolerate legacy column.type shapes:
//  - bare lowercase string (current): "number"
//  - capitalized string: "Number"
//  - SpotSelect option object stored verbatim: { label: "Number", value: "number" }
// The last form leaked into saved schemas before the SpotSelect onChange
// handler was fixed to extract `.value`.
const resolveColType = (column: TableColumn): TableColumn["type"] => {
  const rawType: unknown = column.type;
  const resolved =
    typeof rawType === "string"
      ? rawType
      : typeof rawType === "object" &&
          rawType !== null &&
          "value" in (rawType as Record<string, unknown>)
        ? (rawType as { value: unknown }).value
        : "text";
  return (
    typeof resolved === "string" ? resolved.toLowerCase() : "text"
  ) as TableColumn["type"];
};

const TableCellEditor = ({
  column,
  value,
  onCommit,
  onCommitCells,
  readOnly,
  cellError,
}: {
  column: TableColumn;
  value: unknown;
  onCommit: (v: unknown) => void;
  /** Patch several cells of this row atomically (lookup fill mappings). */
  onCommitCells: (patch: Row) => void;
  readOnly?: boolean;
  cellError?: string | null;
}) => {
  const colType = resolveColType(column);

  // Inset: cell editors fill their <td> exactly, so an outward ring paints
  // under the table's own cell borders and never shows.
  const cellRing = cellError
    ? column.validation?.strict
      ? " ring-1 ring-inset ring-destructive/70 focus:ring-destructive/70 focus-visible:ring-destructive/70"
      : " ring-1 ring-inset ring-warning-500/70 focus:ring-warning-500/70 focus-visible:ring-warning-500/70"
    : "";

  if (colType === "boolean") {
    return (
      <div className="flex items-center h-8 px-2">
        <Switch
          checked={!!value}
          onCheckedChange={(c) => onCommit(c)}
          disabled={readOnly}
        />
      </div>
    );
  }

  if (colType === "select") {
    const lookupSource = lookupSourceOf(column);
    if (lookupSource) {
      return (
        <LookupCellCombobox
          source={lookupSource}
          value={value}
          readOnly={readOnly}
          onSelect={(selected, row) => {
            const patch: Row = { [column.name]: selected };
            for (const mapping of lookupSource.fill ?? []) {
              patch[mapping.to] = row ? (row[mapping.from] ?? null) : null;
            }
            onCommitCells(patch);
          }}
        />
      );
    }
    const stringValue = value != null && value !== "" ? String(value) : "";
    const opts = column.options ?? [];
    return (
      <Select
        value={stringValue || "__none__"}
        onValueChange={(v) => onCommit(v === "__none__" ? null : v)}
        disabled={readOnly}
      >
        <SelectTrigger
          title={stringValue || undefined}
          className={`h-8 w-full min-w-0 border-0 shadow-none bg-transparent focus:ring-0 rounded-none [&>span]:truncate${cellRing}`}
        >
          <SelectValue placeholder="-" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">-</SelectItem>
          {opts.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (colType === "date") {
    const validDate = parseDateOnly(value);
    const displayDate = validDate ? validDate.toLocaleDateString() : "-";
    return (
      <Popover>
        <PopoverTrigger asChild>
          {/* No calendar glyph in the cell — the Tables building block shows
              the type only in the header. */}
          <button
            type="button"
            disabled={readOnly}
            className={`h-8 w-full flex items-center px-3 text-sm font-normal text-muted-foreground bg-transparent hover:bg-muted/30 transition-colors cursor-pointer disabled:cursor-not-allowed${cellRing}`}
          >
            <span className="truncate">{displayDate}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarComponent
            mode="single"
            selected={validDate}
            onSelect={(date) => {
              onCommit(date ? formatDateOnly(date) : null);
            }}
          />
        </PopoverContent>
      </Popover>
    );
  }

  const stringValue = value == null ? "" : String(value);
  return (
    <Input
      type={inputTypeFor(colType)}
      value={stringValue}
      // The input scrolls a long value out of sight; the native tooltip
      // shows the whole of it on hover.
      title={stringValue || undefined}
      // `disabled`, not `readOnly`, so locked text cells get the same dim
      // + no-cursor treatment as boolean/select/date cells next to them.
      disabled={readOnly}
      onChange={(e) => {
        const raw = e.target.value;
        if (colType === "number") {
          onCommit(raw === "" ? null : Number(raw));
        } else {
          onCommit(raw);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.currentTarget.blur();
        }
      }}
      className={`h-8 w-full min-w-0 border-0 shadow-none bg-transparent focus-visible:ring-1 focus-visible:ring-border focus-visible:bg-background rounded-none${cellRing}`}
    />
  );
};

/**
 * Pure controlled grid for a table field. onChange fires per cell keystroke
 * and per row add/remove — the caller is responsible for any debouncing or
 * persistence.
 */
export const TableFieldGrid = ({
  columns,
  rows,
  onChange,
  readOnly,
  widthStorageKey,
}: TableFieldGridProps) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => {
    if (!widthStorageKey || typeof window === "undefined") return {};
    try {
      return parseStoredWidths(localStorage.getItem(widthStorageKey));
    } catch {
      return {};
    }
  });
  const widthsRef = useRef(colWidths);
  widthsRef.current = colWidths;

  const persistWidths = (widths: Record<string, number>) => {
    if (!widthStorageKey) return;
    try {
      localStorage.setItem(widthStorageKey, JSON.stringify(widths));
    } catch {
      // Quota/private-mode failure loses persistence, not the resize.
    }
  };

  // onDragEnd reads the ref from a mouseup handler, so the ref has to be
  // assigned here rather than waiting for the render-time sync: a final
  // mousemove that has not re-rendered yet would otherwise persist stale
  // widths.
  const commitWidths = (next: Record<string, number>) => {
    widthsRef.current = next;
    setColWidths(next);
  };

  const { resizingCol, beginDrag } = useColumnDrag({
    enabled: true,
    computeUpdates: ({ col, widths }, deltaX) =>
      pushWidth({ col, widths, deltaX, min: MIN_FIELD_COL_W }),
    onFirstMove: ({ widths }) =>
      commitWidths({ ...widthsRef.current, ...widths }),
    onChange: (updates) => commitWidths({ ...widthsRef.current, ...updates }),
    onDragEnd: () => persistWidths(widthsRef.current),
  });

  const wrapRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(0);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setContainerW(el.clientWidth));
    ro.observe(el);
    setContainerW(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // Until the user resizes something, the table keeps the browser's auto
  // layout (content-sized columns). The first drag snapshots the rendered
  // widths and switches to fixed layout so widths become explicit.
  const useFixedLayout = Object.keys(colWidths).length > 0 && containerW > 0;
  const fixedW = FIELD_UTILITY_COL_W + (readOnly ? 0 : FIELD_DELETE_COL_W);
  const { dataWidths, tableWidth } = computeFieldColumnWidths({
    columnNames: columns.map((c) => c.name),
    storedWidths: colWidths,
    containerW,
    fixedW,
  });

  const snapshotWidths = () => {
    const snapshot: Record<string, number> = {};
    tableRef.current
      ?.querySelectorAll<HTMLElement>("th[data-col-name]")
      .forEach((th) => {
        const name = th.dataset.colName;
        if (name) snapshot[name] = th.offsetWidth;
      });
    return snapshot;
  };

  const beginResize = (colName: string, startX: number) => {
    const snapshot = snapshotWidths();
    if (snapshot[colName] === undefined) return;
    beginDrag({ col: colName, startX, widths: snapshot });
  };

  // Double-click a resize handle: shrink that column to NARROW_FIELD_COL_W.
  // The last column then absorbs the freed space, so on a table that is not
  // overflowing, narrowing the last column is a no-op.
  const narrowColumnWidth = (colName: string) => {
    // Before the first resize the grid is still in content-sized auto layout;
    // snapshot the rendered widths so the other columns keep theirs across the
    // switch to fixed layout.
    const base = useFixedLayout ? widthsRef.current : snapshotWidths();
    if (base[colName] === NARROW_FIELD_COL_W) return;
    const next = { ...base, [colName]: NARROW_FIELD_COL_W };
    commitWidths(next);
    persistWidths(next);
  };

  if (columns.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No columns configured.
      </div>
    );
  }

  const updateCells = (rowIndex: number, patch: Row) => {
    onChange(
      rows.map((row, i) => (i === rowIndex ? { ...row, ...patch } : row))
    );
  };

  const addRow = () => {
    const emptyRow: Row = {};
    for (const col of columns) {
      emptyRow[col.name] = defaultForType(col.type);
    }
    onChange([...rows, emptyRow]);
  };

  const removeRow = (rowIndex: number) => {
    onChange(rows.filter((_, i) => i !== rowIndex));
  };

  // Later occurrences of a value in a unique column, per column name.
  const duplicateRowsByColumn = new Map<string, Set<number>>();
  for (const col of columns) {
    if (!col.validation?.unique_in_table) continue;
    const seen = new Set<unknown>();
    const duplicates = new Set<number>();
    rows.forEach((row, i) => {
      const cell = row[col.name];
      if (isEmpty(cell)) return;
      if (seen.has(cell)) duplicates.add(i);
      else seen.add(cell);
    });
    if (duplicates.size > 0) duplicateRowsByColumn.set(col.name, duplicates);
  }

  const cellErrorAt = (col: TableColumn, rowIndex: number): string | null => {
    const cell = rows[rowIndex]?.[col.name];
    if (!col.validation) return null;
    const message = validateValue(cell, resolveColType(col), col.validation);
    if (message) return message;
    if (duplicateRowsByColumn.get(col.name)?.has(rowIndex)) {
      return `duplicate value '${String(cell)}' in this table`;
    }
    return null;
  };

  return (
    <div className="flex flex-col gap-2 w-full min-w-0">
      {/* `min-w-0` so a wide table doesn't push our flex/grid ancestors
          wider than their share — only this wrapper gets the horizontal
          scrollbar. */}
      <ColumnResizeOverlay active={resizingCol !== null} />
      <div
        ref={wrapRef}
        className="rounded-md border overflow-x-auto overflow-y-hidden"
      >
        {/* border-separate, not border-collapse: the collapsed model gives the
            borders to the table, so a sticky cell takes only its content along
            and leaves its column behind as the table scrolls. Separated
            borders stay with the cell, at the cost of every divider having to
            live on a cell rather than on the row. */}
        <table
          ref={tableRef}
          className={cn("text-sm border-separate", !useFixedLayout && "w-full")}
          style={{
            borderSpacing: 0,
            ...(useFixedLayout
              ? { tableLayout: "fixed" as const, width: tableWidth }
              : {}),
          }}
        >
          {useFixedLayout && (
            <colgroup>
              <col style={{ width: FIELD_UTILITY_COL_W }} />
              {columns.map((col, ci) => (
                <col key={col.name} style={{ width: dataWidths[ci] }} />
              ))}
              {!readOnly && <col style={{ width: FIELD_DELETE_COL_W }} />}
            </colgroup>
          )}
          {/* Header mirrors the Tables building block: type icon beside each
              label, column dividers, same ground. */}
          <thead className="bg-[#FAFAFA] dark:bg-[oklch(0.28_0_0)]">
            <tr>
              <th
                className="border-b border-r border-border px-2 py-2 text-center text-xs font-medium text-muted-foreground"
                style={{ width: FIELD_UTILITY_COL_W }}
              >
                #
              </th>
              {columns.map((col, ci) => (
                <th
                  key={col.name}
                  data-col-name={col.name}
                  className={cn(
                    "relative border-b border-r border-border px-3 py-2 text-left text-sm font-medium text-foreground last:border-r-0",
                    useFixedLayout ? "overflow-hidden" : "min-w-[120px]",
                    // The frozen delete column owns this separator via its
                    // border-l; drop ours so the line isn't doubled.
                    ci === columns.length - 1 && !readOnly && "border-r-0"
                  )}
                >
                  <div className="flex items-center gap-1.5">
                    <HeaderIcon column={col} />
                    <span className="min-w-0 truncate">{col.label}</span>
                  </div>
                  <ColumnResizeHandle
                    label={`Resize ${col.label} column`}
                    isResizing={resizingCol === col.name}
                    inset
                    onDragStart={(startX) => beginResize(col.name, startX)}
                    onNarrow={() => narrowColumnWidth(col.name)}
                  />
                </th>
              ))}
              {/* Delete column: frozen to the right edge, like the Tables
                  grid's add-column cell. Its border-l is the single
                  separator, so the last data cell drops its border-r. */}
              {!readOnly && (
                <th
                  className="sticky right-0 z-20 border-b border-l border-border bg-[#FAFAFA] dark:bg-[oklch(0.28_0_0)]"
                  style={{ width: FIELD_DELETE_COL_W }}
                />
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="group hover:bg-muted/10">
                <td
                  className="border-b border-r border-border px-2 text-center text-xs text-muted-foreground align-middle group-last:border-b-0"
                  style={{ width: FIELD_UTILITY_COL_W }}
                >
                  {rowIndex + 1}
                </td>
                {columns.map((col, ci) => {
                  const cellError = cellErrorAt(col, rowIndex);
                  const editor = (
                    <TableCellEditor
                      column={col}
                      value={row[col.name]}
                      onCommit={(v) => updateCells(rowIndex, { [col.name]: v })}
                      onCommitCells={(patch) => updateCells(rowIndex, patch)}
                      readOnly={readOnly || col.read_only}
                      cellError={cellError}
                    />
                  );
                  return (
                    <td
                      key={col.name}
                      className={cn(
                        "border-b border-r border-border align-middle last:border-r-0 group-last:border-b-0",
                        ci === columns.length - 1 && !readOnly && "border-r-0"
                      )}
                    >
                      {cellError ? (
                        <NoxusTooltip
                          content={sentenceCase(cellError)}
                          asChild
                          classNameTrigger="block w-full"
                        >
                          <div className="relative h-full w-full">
                            {editor}
                            <span
                              className={`pointer-events-none absolute right-0 top-0 h-0 w-0 border-t-[7px] border-l-[7px] border-l-transparent ${
                                col.validation?.strict
                                  ? "border-t-destructive"
                                  : "border-t-warning-500"
                              }`}
                            />
                          </div>
                        </NoxusTooltip>
                      ) : (
                        editor
                      )}
                    </td>
                  );
                })}
                {!readOnly && (
                  <td
                    className="sticky right-0 z-10 border-b border-l border-border bg-background text-center align-middle group-last:border-b-0 group-hover:bg-muted/10"
                    style={{ width: FIELD_DELETE_COL_W }}
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeRow(rowIndex)}
                      aria-label={`Remove row ${rowIndex + 1}`}
                      className="mx-auto h-7 w-7 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {/* Outside the table: a cell is as wide as the table, so a centred
            empty state drifts off-screen once the grid scrolls. A block here
            is only as wide as the viewport, and sticky keeps it there. */}
        {rows.length === 0 && (
          <div className="sticky left-0 flex items-center justify-center">
            {/* Same empty state as the Tables building block. */}
            <NoxusEmptyBlock
              size="sm"
              icon={Rows3}
              title="No rows yet"
              description="Add your first row, or write to this table from a flow or agent."
              button={!readOnly ? "Add row" : undefined}
              buttonIcon={Plus}
              onClick={!readOnly ? addRow : undefined}
            />
          </div>
        )}
      </div>
      {/* When empty, the empty state carries the Add row action. */}
      {!readOnly && rows.length > 0 && (
        <Button
          type="button"
          variant="outline"
          onClick={addRow}
          className="h-8 w-fit gap-1.5 px-3 font-normal"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add row
        </Button>
      )}
    </div>
  );
};
